import { useSelector } from 'react-redux';

interface FileSystemTreeNode {
  type?: string;
  children?: Record<string, FileSystemTreeNode>;
}

interface TransformationEntityState {
  currentGraph?: {
    metainfo?: {
      pipelines?: Record<string, unknown>;
      fileSystem?: { children?: Record<string, FileSystemTreeNode> };
    };
  };
}

const TRANSFORMATION_ENTITY_TYPES = new Set(['Pipeline', 'Model', 'Snapshot']);

function hasTransformationEntitiesInTree(children: Record<string, FileSystemTreeNode>): boolean {
  for (const child of Object.values(children)) {
    if (child.type && TRANSFORMATION_ENTITY_TYPES.has(child.type)) {
      return true;
    }

    if (child.type === 'Folder' && child.children && Object.keys(child.children).length > 0) {
      if (hasTransformationEntitiesInTree(child.children)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Returns whether the current SQL project has transformation entities (pipelines or models).
 *
 * Page-agnostic: uses minimal structural types and raw Redux state access instead of
 * importing SQL page-level modules, avoiding common → page hierarchy violations.
 *
 * Safe to call from non-SQL contexts (e.g. Fabric pages with a minimal fake Redux store):
 * gracefully handles missing state by returning false.
 */
export function useHasTransformationEntities() {
  return useSelector<TransformationEntityState, boolean>((state) => {
    const metainfo = state.currentGraph?.metainfo;
    if (!metainfo) return false;

    const { pipelines, fileSystem } = metainfo;

    if (pipelines && Object.keys(pipelines).length > 0) return true;

    if (!fileSystem?.children || Object.keys(fileSystem.children).length === 0) return false;

    return hasTransformationEntitiesInTree(fileSystem.children);
  });
}
